import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { ArrowDown, ArrowUp, Minus, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Kbd } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ACTIVITY_DETAIL_MAX, planTiming, type DraftActivity } from "@/components/courses/unit-planner";
import { useRovingFocus } from "@/hooks/use-roving-focus";
import { formatInCourseZone } from "@/lib/session-portal";

const MAX_ACTIVITIES = 40;
const MAX_MINUTES = 1440;
const STEP = 5;

const number = (index: number) => String(index + 1).padStart(2, "0");
const minutesOf = (activity: DraftActivity) =>
  /^\d+$/.test(activity.minutes) ? Number(activity.minutes) : null;

/** Per viewer, per browser. A denied store simply keeps the default. */
function useTimingsPreference(storageKey: string) {
  const [shown, setShown] = useState(() => {
    try { return localStorage.getItem(storageKey) !== "hidden"; } catch { return true; }
  });
  const toggle = () => setShown(current => {
    const next = !current;
    try { localStorage.setItem(storageKey, next ? "shown" : "hidden"); } catch { /* preference only */ }
    return next;
  });
  return [shown, toggle] as const;
}

/**
 * The session's budget in words and as a bar. The budget is the session's own
 * scheduled length; without both instants the figure says so rather than
 * guessing one.
 */
function outlineBudget({ activities, startsAt, endsAt, shown, clock }: {
  activities: DraftActivity[]; startsAt: string | null; endsAt: string | null; shown: boolean; clock: boolean;
}) {
  const timing = planTiming(activities.map(activity => ({ id: activity.id, label: activity.label, minutes: minutesOf(activity) })), startsAt, endsAt);
  const over = timing.remaining !== null && timing.remaining < 0 ? -timing.remaining : 0;
  const figure = !clock
    ? `${timing.planned} min planned`
    : timing.budget === null
      ? `${timing.planned} min planned, session length unavailable`
      : `${timing.planned} of ${timing.budget} min${over ? `, ${over} over` : ""}`;
  const untimed = timing.untimed ? `, ${timing.untimed} untimed` : "";
  const timed = activities.map(minutesOf).filter((minutes): minutes is number => minutes !== null && minutes > 0);
  const slack = timing.budget === null ? 0 : Math.max(0, timing.budget - timing.planned);
  return {
    figure: (
      <span data-semantic="bento.planner.timing" data-over={over > 0 || undefined} className={`font-data text-meta tabular-nums ${over ? "text-status-warning-foreground" : "text-muted-foreground"}`}>
        {figure}{untimed}
      </span>
    ),
    bar: shown && (timed.length > 0 || slack > 0) ? (
      <div aria-hidden data-over={over > 0 || undefined} className="flex h-1 w-full gap-0.5">
        {timed.map((minutes, index) => (
          <span key={index} style={{ flexGrow: minutes }} className={`min-w-0.5 basis-0 rounded-full ${over ? "bg-status-warning" : "bg-primary-soft"}`} />
        ))}
        {slack > 0 && <span style={{ flexGrow: slack }} className="basis-0 rounded-full bg-rule" />}
      </div>
    ) : null,
  };
}

/** Clock time of each row: the session start plus every minute before it. Blank once an earlier row is untimed. */
function clockTimes(activities: DraftActivity[], startsAt: string | null, timezone: string) {
  let elapsed: number | null = startsAt ? 0 : null;
  return activities.map(activity => {
    const at = elapsed === null || !startsAt ? "" : formatInCourseZone(new Date(Date.parse(startsAt) + elapsed * 60000).toISOString(), timezone, { hour: "numeric", minute: "2-digit" });
    const minutes = minutesOf(activity);
    elapsed = elapsed === null || minutes === null ? null : elapsed + minutes;
    return at;
  });
}

function RowSummary({ activity, index, time, shown, clock }: { activity: DraftActivity; index: number; time: string; shown: boolean; clock: boolean }) {
  const minutes = minutesOf(activity);
  return (
    <>
      <span className="font-data text-meta tabular-nums text-muted-foreground">{number(index)}</span>
      {shown && clock && <span className="hidden font-data text-meta tabular-nums text-muted-foreground sm:block">{time}</span>}
      <span className="grid min-w-0 gap-0.5">
        {shown && clock && time && <span className="font-data text-meta tabular-nums text-muted-foreground sm:hidden">{time}</span>}
        <span className="text-ui font-semibold text-foreground [overflow-wrap:anywhere]">{activity.label.trim() || "Untitled activity"}</span>
        {activity.detail.trim() && <span className="text-meta text-muted-foreground [overflow-wrap:anywhere] whitespace-pre-wrap">{activity.detail.trim()}</span>}
      </span>
      {shown && <span className="font-data text-meta tabular-nums text-muted-foreground">{minutes === null ? "Untimed" : `${minutes} min`}</span>}
    </>
  );
}

const ROW_GRID = (shown: boolean, clock: boolean) => !shown
  ? "grid-cols-[1.75rem_minmax(0,1fr)]"
  : clock
    ? "grid-cols-[1.75rem_minmax(0,1fr)_auto] sm:grid-cols-[1.75rem_4.5rem_minmax(0,1fr)_auto]"
    : "grid-cols-[1.75rem_minmax(0,1fr)_auto]";

/**
 * The teaching outline: what happens in session, in order, with optional
 * minutes against the session's own length. Private to the teaching team.
 * Editable, a row opens in place; read-only, the same rows are plain text.
 * A plan on no session has no clock: its rows carry minutes but no start times.
 */
export function ClassPlanOutline({
  idPrefix,
  activities,
  onChange,
  editToken,
  startsAt,
  endsAt,
  timezone,
  timingsKey,
  clock = true,
  heading: Heading = "h3",
}: {
  idPrefix: string;
  activities: DraftActivity[];
  /** Absent when the plan is read-only. An updater applies to the latest
   *  draft, so a field edit never writes back a list one keystroke behind. */
  onChange?: (next: DraftActivity[] | ((current: DraftActivity[]) => DraftActivity[])) => void;
  /** Any value that changes with every edit to the plan; it retires a pending Undo. */
  editToken?: string;
  startsAt: string | null;
  endsAt: string | null;
  timezone: string;
  timingsKey: string;
  /** False for a plan on no session: no clock times and no session length. */
  clock?: boolean;
  /** One level below the card's own heading. */
  heading?: "h3" | "h4" | "h5";
}) {
  const [shown, toggleTimings] = useTimingsPreference(timingsKey);
  const [openId, setOpenId] = useState<string | null>(null);
  /** The current row: the one last focused, which list keys act on. */
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [removed, setRemoved] = useState<{ activity: DraftActivity; index: number } | null>(null);
  const focusTarget = useRef<string | null>(null);
  const focusFrom = useRef<Element | null>(null);
  const outline = useRef<HTMLElement | null>(null);
  const focusNext = (target: string) => {
    focusTarget.current = target;
    focusFrom.current = document.activeElement;
  };
  useEffect(() => () => { focusTarget.current = null; focusFrom.current = null; }, []);
  const ownEdit = useRef(false);
  const lastToken = useRef(editToken);
  // Retiring an Undo that is not there is no write at all: a setState with an
  // unchanged value still queues a render, and a burst of fast input (dictation,
  // a typing tool) that leaves queued work after every commit trips React's
  // update-depth guard, which throws from the field's own change handler and
  // drops the keystroke.
  useEffect(() => {
    if (editToken === lastToken.current) return;
    lastToken.current = editToken;
    if (ownEdit.current) ownEdit.current = false;
    else if (removed) setRemoved(null);
  }, [editToken, removed]);
  useEffect(() => {
    if (!focusTarget.current) return;
    const origin = focusFrom.current;
    const active = document.activeElement;
    const retiredControl = active === document.body && origin && (!origin.isConnected || origin.matches(":disabled"));
    if (active !== origin && !retiredControl || active !== document.body && !outline.current?.contains(active)) {
      focusTarget.current = null;
      return;
    }
    const target = document.getElementById(focusTarget.current);
    if (!target) return;
    target.focus();
    focusTarget.current = null;
  });

  const currentIndex = activities.findIndex(activity => activity.id === (openId ?? currentId));
  const roving = useRovingFocus<HTMLButtonElement>({ count: activities.length, orientation: "vertical", activeIndex: currentIndex });
  const budget = outlineBudget({ activities, startsAt, endsAt, shown, clock });
  const times = clockTimes(activities, startsAt, timezone);
  const id = (activityId: string, part: string) => `${idPrefix}-${activityId}-${part}`;

  const commit = (next: DraftActivity[] | ((current: DraftActivity[]) => DraftActivity[]), keepUndo = false) => {
    ownEdit.current = keepUndo;
    if (!keepUndo && removed) setRemoved(null);
    onChange?.(next);
  };
  const update = (index: number, patch: Partial<DraftActivity>) => {
    const target = activities[index]?.id;
    commit(current => current.map(activity => activity.id === target ? { ...activity, ...patch } : activity));
  };
  const step = (index: number, delta: number) => {
    const current = minutesOf(activities[index]);
    if (current === null && delta < 0) return;
    update(index, { minutes: String(Math.min(MAX_MINUTES, Math.max(0, (current ?? 0) + delta))) });
  };
  const move = (index: number, direction: -1 | 1, fromRow = false) => {
    const landed = index + direction;
    if (landed < 0 || landed >= activities.length) return;
    const next = [...activities];
    [next[index], next[landed]] = [next[landed], next[index]];
    // From the row, focus stays on the row. From a move control, a control
    // disabled at its new edge would drop focus; land on the other one.
    const towardEdge = direction === -1 ? landed === 0 : landed === next.length - 1;
    focusNext(id(next[landed].id, fromRow ? "summary" : towardEdge ? (direction === -1 ? "down" : "up") : (direction === -1 ? "up" : "down")));
    commit(next);
  };
  const remove = (index: number) => {
    const activity = activities[index];
    setOpenId(null);
    setRemoved({ activity, index });
    focusNext(`${idPrefix}-undo`);
    commit(activities.filter((_, i) => i !== index), true);
  };
  const undo = () => {
    if (!removed) return;
    const next = [...activities];
    next.splice(Math.min(removed.index, next.length), 0, removed.activity);
    focusNext(id(removed.activity.id, "summary"));
    commit(next);
  };
  const add = (after = activities.length - 1) => {
    if (activities.length >= MAX_ACTIVITIES) return;
    const activityId = crypto.randomUUID();
    const next = [...activities];
    next.splice(after + 1, 0, { id: activityId, label: "", minutes: "", detail: "" });
    setOpenId(activityId);
    setCurrentId(activityId);
    focusNext(id(activityId, "label"));
    commit(next);
  };
  /** The outline's list keys, on a row. Text fields never see them, and Ctrl or Cmd leaves them to the browser. */
  const rowKey = (event: KeyboardEvent<HTMLButtonElement>, index: number, activityId: string) => {
    if (event.ctrlKey || event.metaKey) return;
    const vertical = event.key === "ArrowUp" || event.key === "ArrowDown";
    if (event.altKey) {
      if (!vertical) return;
      event.preventDefault();
      move(index, event.key === "ArrowUp" ? -1 : 1, true);
      return;
    }
    if (event.key === "+" || event.key === "=") { event.preventDefault(); step(index, STEP); return; }
    if (event.key === "-" || event.key === "_") { event.preventDefault(); step(index, -STEP); return; }
    if (event.key === "n" || event.key === "N") { event.preventDefault(); add(index); return; }
    if (event.key === "Escape" && openId === activityId) { event.preventDefault(); setOpenId(null); return; }
    roving.itemProps(index).onKeyDown(event);
  };
  const close = (event: KeyboardEvent, activityId: string) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    setOpenId(null);
    focusNext(id(activityId, "summary"));
  };

  const undoRow = removed && (
    <li key="undo" className="flex flex-wrap items-center gap-x-2 border-t border-rule py-2 text-ui text-muted-foreground first:border-t-0">
      <span role="status">Activity removed.</span>
      <Button id={`${idPrefix}-undo`} type="button" variant="link" size="sm" className="px-0" onClick={undo}>Undo</Button>
    </li>
  );
  const rows = activities.map((activity, index) => {
    const open = openId === activity.id;
    const grid = `grid items-start gap-x-3 py-2.5 text-start ${ROW_GRID(shown, clock)}`;
    return (
      <li key={activity.id} className="border-t border-rule first:border-t-0">
        {onChange ? (
          <button
            type="button"
            {...roving.itemProps(index)}
            onFocus={event => { roving.itemProps(index).onFocus(event); setCurrentId(activity.id); }}
            onKeyDown={event => rowKey(event, index, activity.id)}
            aria-keyshortcuts="ArrowUp ArrowDown Alt+ArrowUp Alt+ArrowDown + - N Enter Escape"
            id={id(activity.id, "summary")}
            aria-expanded={open}
            aria-controls={open ? id(activity.id, "panel") : undefined}
            onClick={() => setOpenId(open ? null : activity.id)}
            className={`${grid} -mx-2 w-[calc(100%+1rem)] rounded-control px-2 outline-none hover:bg-well focus-visible:ring-2 focus-visible:ring-ring`}
          >
            <RowSummary activity={activity} index={index} time={times[index]} shown={shown} clock={clock} />
          </button>
        ) : (
          <div className={`${grid} w-full`}><RowSummary activity={activity} index={index} time={times[index]} shown={shown} clock={clock} /></div>
        )}
        {open && onChange && (
          <div
            id={id(activity.id, "panel")}
            role="group"
            aria-label={`Edit activity ${number(index)}`}
            onKeyDown={event => close(event, activity.id)}
            className="grid gap-3 pb-3 sm:ps-10"
          >
            <div className="grid gap-1.5">
              <Label htmlFor={id(activity.id, "label")}>Activity title</Label>
              <Input id={id(activity.id, "label")} maxLength={200} value={activity.label} placeholder="Name this activity" onChange={event => update(index, { label: event.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={id(activity.id, "detail")}>Detail <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Textarea id={id(activity.id, "detail")} rows={1} maxLength={ACTIVITY_DETAIL_MAX} value={activity.detail} placeholder="What you will do or ask" className="min-h-[var(--control-height)] resize-none" onChange={event => update(index, { detail: event.target.value })} />
            </div>
            <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
              <div role="group" aria-label="Minutes" className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="icon-sm" aria-label={`Shorten by ${STEP} minutes`} disabled={minutesOf(activity) === null || minutesOf(activity) === 0} onClick={() => step(index, -STEP)}><Minus aria-hidden /></Button>
                <Input
                  id={id(activity.id, "minutes")}
                  aria-label="Minutes"
                  inputMode="numeric"
                  placeholder="Untimed"
                  value={activity.minutes}
                  onChange={event => update(index, { minutes: event.target.value.trim() })}
                  className="h-[var(--control-height-compact)] w-20 text-center font-data tabular-nums"
                />
                <Button type="button" variant="ghost" size="icon-sm" aria-label={`Lengthen by ${STEP} minutes`} disabled={(minutesOf(activity) ?? 0) >= MAX_MINUTES} onClick={() => step(index, STEP)}><Plus aria-hidden /></Button>
              </div>
              <Button id={id(activity.id, "up")} type="button" variant="ghost" size="icon-sm" aria-label="Move activity up" disabled={index === 0} onClick={() => move(index, -1)}><ArrowUp aria-hidden /></Button>
              <Button id={id(activity.id, "down")} type="button" variant="ghost" size="icon-sm" aria-label="Move activity down" disabled={index === activities.length - 1} onClick={() => move(index, 1)}><ArrowDown aria-hidden /></Button>
              <Button type="button" variant="ghost" size="sm" className="ms-auto text-destructive" onClick={() => remove(index)}>Remove</Button>
            </div>
          </div>
        )}
      </li>
    );
  });
  if (undoRow && removed) rows.splice(Math.min(removed.index, rows.length), 0, undoRow);

  return (
    <section ref={outline} data-semantic="bento.planner.outline" aria-labelledby={`${idPrefix}-outline`} className="grid gap-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Heading id={`${idPrefix}-outline`} className="text-ui font-semibold text-foreground">Teaching outline</Heading>
        <span className="text-meta text-muted-foreground">Teaching team only</span>
        <div className="ms-auto flex items-center gap-2">
          <Button type="button" variant="link" size="sm" className="px-0" onClick={toggleTimings}>{shown ? "Hide timings" : "Show timings"}</Button>
          {budget.figure}
        </div>
      </div>
      {budget.bar}
      {activities.length === 0 && !removed ? (
        <p className="text-ui text-muted-foreground">{onChange ? "No activities yet. Add the first step toward the objective." : "No activities planned."}</p>
      ) : (
        <ol aria-label="Teaching outline" className="grid">{rows}</ol>
      )}
      {onChange && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <Button type="button" variant="subtle" size="sm" disabled={activities.length >= MAX_ACTIVITIES} onClick={() => add()}>
            <Plus aria-hidden /> Add activity
          </Button>
          {activities.length > 0 && (
            <p className="hidden flex-wrap items-center gap-x-1.5 gap-y-1 text-meta text-muted-foreground pointer-fine:flex">
              <Kbd>↑</Kbd><Kbd>↓</Kbd> select, <Kbd>Alt</Kbd><Kbd>↑</Kbd><Kbd>↓</Kbd> move, <Kbd>+</Kbd><Kbd>-</Kbd> time,
              <Kbd>Enter</Kbd> edit, <Kbd>N</Kbd> new.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
